import type { CampaignTemplateVariableSource } from '@workspace/types';

const MAX_COMPONENTS = 50;
const MAX_TEXT_LENGTH = 4096;

export const CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS: Record<
  CampaignTemplateVariableSource,
  string
> = {
  'contact.firstName': 'Nombre del contacto',
  'contact.lastName': 'Apellido del contacto',
  'contact.businessName': 'Razon social',
  'contact.phoneE164': 'Telefono',
  'contact.email': 'Email',
  'debt.invoiceNumber': 'Numero de factura',
  'debt.balance': 'Saldo pendiente',
  'debt.dueDate': 'Fecha de vencimiento',
  'shipment.tracking': 'Tracking',
  'shipment.eta': 'ETA',
  'shipment.address': 'Direccion de entrega',
  'invoice.number': 'Comprobante',
  'invoice.amount': 'Importe',
  'campaign.accessUrl': 'Link al chat de cobranzas',
};

export const CONTACT_TEMPLATE_VARIABLE_SOURCES: CampaignTemplateVariableSource[] = [
  'contact.firstName',
  'contact.lastName',
  'contact.businessName',
  'contact.phoneE164',
  'contact.email',
];

export const COBRANZAS_TEMPLATE_VARIABLE_SOURCES: CampaignTemplateVariableSource[] = [
  ...CONTACT_TEMPLATE_VARIABLE_SOURCES,
  'campaign.accessUrl',
  'debt.invoiceNumber',
  'debt.balance',
  'debt.dueDate',
];

export const NOTICE_TEMPLATE_VARIABLE_SOURCES: CampaignTemplateVariableSource[] = [
  ...CONTACT_TEMPLATE_VARIABLE_SOURCES,
  'shipment.tracking',
  'shipment.eta',
  'shipment.address',
  'invoice.number',
  'invoice.amount',
];

const NAMED_KEY_SET = new Set<string>([
  ...COBRANZAS_TEMPLATE_VARIABLE_SOURCES,
  ...NOTICE_TEMPLATE_VARIABLE_SOURCES,
]);
const NAMED_BODY_MAX = 8000;
const NAMED_KEY_MAX = 64;
const NAMED_KEYS_MAX = 20;
const NAMED_VALUE_MAX = 200;
const ACCESS_URL_VALUE_MAX = 2048;

function valueMax(key: string): number {
  return key === 'campaign.accessUrl' ? ACCESS_URL_VALUE_MAX : NAMED_VALUE_MAX;
}

export const MESSAGE_TEMPLATE_PREVIEW_VALUES: Record<string, string> = {
  'contact.firstName': 'Ada',
  'contact.lastName': 'Lovelace',
  'contact.businessName': 'Ejemplo SRL',
  'contact.phoneE164': '+54911000000',
  'contact.email': 'ada@example.com',
  'debt.invoiceNumber': 'F-1001',
  'debt.balance': '15000',
  'debt.dueDate': '2026-08-01',
  'shipment.tracking': 'PKG-9981',
  'shipment.eta': 'Hoy 18:00',
  'shipment.address': 'Av. Ejemplo 123',
  'invoice.number': 'A-0001-00004501',
  'invoice.amount': '1240500',
  'campaign.accessUrl': 'https://chat.ejemplo.com/chatbot/00000000-0000-0000-0000-000000000001#t=preview',
};

export function isNamedTemplateKey(value: string): value is CampaignTemplateVariableSource {
  return NAMED_KEY_SET.has(value.slice(0, NAMED_KEY_MAX));
}

export function extractNamedTemplateKeys(...parts: Array<string | null | undefined>): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    collectNamedKeys(part, keys, seen);
  }
  return keys.slice(0, NAMED_KEYS_MAX);
}

export function unknownNamedTemplateKeys(...parts: Array<string | null | undefined>): string[] {
  const unknown: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const bounded = (part ?? '').slice(0, NAMED_BODY_MAX);
    let pos = 0;
    while (pos < bounded.length) {
      const token = nextMustacheToken(bounded, pos);
      if (!token) {
        break;
      }
      if (!NAMED_KEY_SET.has(token.inner) && !seen.has(token.inner)) {
        seen.add(token.inner);
        unknown.push(token.inner);
      }
      pos = token.nextPos;
    }
  }
  return unknown.slice(0, NAMED_KEYS_MAX);
}

export function renderNamedTemplate(text: string, values: Record<string, string>): string {
  const bounded = text.slice(0, NAMED_BODY_MAX);
  let result = '';
  let pos = 0;
  while (pos < bounded.length) {
    const token = nextMustacheToken(bounded, pos);
    if (!token) {
      result += bounded.slice(pos);
      break;
    }
    result += bounded.slice(pos, token.start);
    const replacement = values[token.inner];
    result +=
      replacement === undefined
        ? bounded.slice(token.start, token.end)
        : String(replacement).slice(0, valueMax(token.inner));
    pos = token.nextPos;
  }
  return result.slice(0, NAMED_BODY_MAX);
}

function collectNamedKeys(part: string | null | undefined, keys: string[], seen: Set<string>): void {
  const bounded = (part ?? '').slice(0, NAMED_BODY_MAX);
  let pos = 0;
  while (pos < bounded.length && keys.length < NAMED_KEYS_MAX) {
    const token = nextMustacheToken(bounded, pos);
    if (!token) {
      break;
    }
    if (NAMED_KEY_SET.has(token.inner) && !seen.has(token.inner)) {
      seen.add(token.inner);
      keys.push(token.inner);
    }
    pos = token.nextPos;
  }
}

function nextMustacheToken(
  text: string,
  from: number,
): { start: number; end: number; nextPos: number; inner: string } | null {
  const start = text.indexOf('{{', from);
  if (start === -1) {
    return null;
  }
  const close = text.indexOf('}}', start + 2);
  if (close === -1) {
    return null;
  }
  return {
    start,
    end: close + 2,
    nextPos: close + 2,
    inner: text.slice(start + 2, close).trim().slice(0, NAMED_KEY_MAX),
  };
}

export function extractTemplateVariableIndices(
  components: Array<Record<string, unknown>>,
): number[] {
  const indices = new Set<number>();
  for (const component of components.slice(0, MAX_COMPONENTS)) {
    const type = String(component.type ?? '').toUpperCase();
    if (type !== 'BODY' && type !== 'HEADER') {
      continue;
    }
    const text = String(component.text ?? '').slice(0, MAX_TEXT_LENGTH);
    let pos = 0;
    while (pos < text.length) {
      const start = text.indexOf('{{', pos);
      if (start === -1) {
        break;
      }
      const end = text.indexOf('}}', start + 2);
      if (end === -1) {
        break;
      }
      const inner = text.slice(start + 2, end).trim();
      if (/^\d{1,2}$/.test(inner)) {
        indices.add(Number(inner));
      }
      pos = end + 2;
    }
  }
  return [...indices].sort((a, b) => a - b);
}
